import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, MapPin, Send, UserCheck, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  fetchEscalatedPickups,
  assignPickup,
  offerPickup,
} from '../services/adminService'
import type { EscalatedPickup, AssignmentCandidate } from '../types'

/**
 * Pickups the offer engine could not place on its own.
 *
 * A pickup is offered to one collector, who gets twelve hours and a reminder. If
 * they never answer it is passed to the next collector who can be reached, for
 * as long as rounds remain and a fresh window would still end a lead before the
 * pickup. When that runs out — nobody reachable in range, every round used, or
 * the slot too close — the job lands here instead, with the nearest collectors
 * who have not already been asked, so an admin can place it in one click rather
 * than opening the pickup, reading the history, and working out who is free.
 *
 * Renders nothing at all when the queue is empty, which is the normal state.
 */

function waitingFor(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ${minutes % 60}m`
  return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

interface ShortlistRowProps {
  candidate: AssignmentCandidate
  rank: number
  busy: boolean
  onAssign: () => void
  onOffer: () => void
}

function ShortlistRow({ candidate, rank, busy, onAssign, onOffer }: ShortlistRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2 bg-white">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#1A1A2E] truncate">
          <span className="mr-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
            {rank}
          </span>
          {candidate.name}
        </p>
        <p className="text-[11px] text-[#6B7280] flex flex-wrap items-center gap-x-2">
          {candidate.distanceKm != null ? (
            <span className="font-semibold text-[#1A1A2E]">{candidate.distanceKm} km away</span>
          ) : (
            <span className="italic">distance unknown</span>
          )}
          <span>
            {candidate.dayLoad}/{candidate.maxDailyPickups} booked
          </span>
          {candidate.ratingCount > 0 && (
            <span>
              {candidate.averageRating}★ ({candidate.ratingCount})
            </span>
          )}
          {candidate.companyName && <span className="text-purple-700">{candidate.companyName}</span>}
          {candidate.reachable === false && (
            <span className="text-amber-700" title="No device registered for push, or pickup alerts switched off — phone them">
              no push
            </span>
          )}
          {candidate.zoneMatch && (
            <span className="inline-flex items-center gap-0.5 text-blue-700">
              <MapPin size={9} /> zone
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          type="button"
          disabled={busy}
          onClick={onOffer}
          title="Offer it to them — they still choose whether to accept"
          className="flex items-center gap-1 text-[11px] font-semibold text-[#6B7280] bg-gray-50 border border-gray-200 px-2 py-1 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
        >
          <Send size={11} />
          Offer
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onAssign}
          title="Place the pickup with them now, without waiting for them to accept"
          className="flex items-center gap-1 text-[11px] font-semibold text-white bg-primary-600 px-2.5 py-1 rounded-lg hover:bg-primary-700 transition disabled:opacity-50"
        >
          <UserCheck size={11} />
          Assign
        </button>
      </div>
    </div>
  )
}

interface EscalationQueueProps {
  /** Called after a pickup leaves the queue, so the parent can refresh too. */
  onResolved?: () => void
}

export default function EscalationQueue({ onResolved }: EscalationQueueProps) {
  const [pickups, setPickups] = useState<EscalatedPickup[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await fetchEscalatedPickups(20)
      setPickups(data?.pickups ?? [])
    } catch {
      toast.error('Failed to load the escalation queue')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const place = async (
    pickup: EscalatedPickup,
    candidate: AssignmentCandidate,
    direct: boolean,
  ) => {
    setBusyId(pickup.id)
    try {
      if (direct) {
        await assignPickup(pickup.id, candidate.collectorId)
        toast.success(`Assigned to ${candidate.name}.`)
      } else {
        await offerPickup(pickup.id, candidate.collectorId)
        toast.success(`Offered to ${candidate.name}.`)
      }
      // Drop it locally first: the pickup has left the queue either way, and
      // waiting for the refetch leaves a resolved row on screen looking live.
      setPickups((current) => current.filter((p) => p.id !== pickup.id))
      onResolved?.()
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message
      toast.error(message || 'Failed to place the pickup')
      load()
    } finally {
      setBusyId(null)
    }
  }

  // The empty queue is the healthy one, and an empty panel every day trains
  // people to stop looking at this part of the page.
  if (loading || pickups.length === 0) return null

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600" />
          <div>
            <h3 className="text-sm font-bold text-amber-900">
              {pickups.length === 1
                ? '1 pickup needs you to assign it'
                : `${pickups.length} pickups need you to assign them`}
            </h3>
            <p className="text-[11px] text-amber-700">
              The collector each was offered to never answered, even after a reminder, and nobody else
              could be reached automatically in time.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="flex items-center gap-1.5 text-xs font-semibold text-amber-900 bg-amber-100 px-3 py-2 rounded-xl hover:bg-amber-200 transition-colors flex-shrink-0"
        >
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>

      <div className="divide-y divide-amber-100">
        {pickups.map((pickup) => {
          const ignoredBy = pickup.assignments.filter((a) => a.status === 'EXPIRED')
          return (
            <div key={pickup.id} className="px-5 py-4 space-y-3">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1A1A2E]">
                    <span className="font-mono text-primary-700 mr-2">
                      {pickup.id.slice(0, 8)}
                    </span>
                    {pickup.address}
                  </p>
                  <p className="text-[11px] text-[#6B7280]">
                    {pickup.wasteType} · {pickup.numberOfBins} bin
                    {pickup.numberOfBins === 1 ? '' : 's'} · scheduled{' '}
                    {new Date(pickup.scheduledDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                    })}
                    {pickup.timeSlot ? ` (${pickup.timeSlot})` : ''}
                    {pickup.resident ? ` · ${pickup.resident.name}` : ''}
                  </p>
                  {ignoredBy.length > 0 && (
                    <p className="text-[11px] text-amber-800 mt-1">
                      No answer from{' '}
                      {ignoredBy.map((a) => a.collector.name).join(', ')}
                      {ignoredBy.some((a) => a.remindedAt) ? ' (reminded)' : ''}
                    </p>
                  )}
                </div>
                <span className="text-[11px] font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded-lg whitespace-nowrap">
                  waiting {waitingFor(pickup.escalatedAt)}
                </span>
              </div>

              {pickup.shortlist.length === 0 ? (
                <p className="text-xs text-amber-800 italic">
                  No other collector is available, in range, and under capacity. Free someone up,
                  or contact the resident to reschedule.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wide">
                    Nearest {pickup.shortlist.length} available
                  </p>
                  {pickup.shortlist.map((candidate, index) => (
                    <ShortlistRow
                      key={candidate.collectorId}
                      candidate={candidate}
                      rank={index + 1}
                      busy={busyId === pickup.id}
                      onAssign={() => place(pickup, candidate, true)}
                      onOffer={() => place(pickup, candidate, false)}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
